/* eslint-disable max-lines */
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useNavigate } from 'react-router';

import { paths } from '@/config/paths';
import { userKeys } from '@/features/user/api/queries';
import { clearUserIdentity } from '@/lib/user-identity';
import { noop } from '@/lib/utils';
import { useNotifications } from '@/stores/notifications';

import { login, type LoginDetails } from './login';
import { logout } from './logout';
import {
  sendOTP,
  resendOTP,
  submitOTP,
  type SendOTP as SendOTPData,
  type SubmitOTP as SubmitOTPData,
} from './otp';
import {
  register,
  getRegistrationDetails,
  type RegisterDetails,
} from './register';
import { resetPassword, type ResetPasswordDetails } from './reset-password';

// Query Keys for Auth only
const authKeys = {
  all: ['auth'] as const,
  status: () => [...authKeys.all, 'status'] as const,
  login: () => [...authKeys.all, 'login'] as const,
  logout: () => [...authKeys.all, 'logout'] as const,
  register: () => [...authKeys.all, 'register'] as const,
  registrationDetails: () => [...authKeys.all, 'registrationDetails'] as const,
  resetPassword: () => [...authKeys.all, 'resetPassword'] as const,
  otp: () => [...authKeys.all, 'otp'] as const,
  sendOtp: () => [...authKeys.otp(), 'send'] as const,
  resendOtp: () => [...authKeys.otp(), 'resend'] as const,
  submitOtp: () => [...authKeys.otp(), 'submit'] as const,
} as const;

// Login Mutation (only handles authentication)
const useLogin = ({
  onSuccess,
  onError,
}: {
  onSuccess?: () => void;
  onError?: () => void;
} = {}) => {
  const queryClient = useQueryClient();
  const { addNotification } = useNotifications();

  return useMutation({
    mutationFn: (data: LoginDetails) => login(data),
    mutationKey: authKeys.login(),
    onSuccess: () => {
      // Only invalidate auth status, user data is handled elsewhere
      void queryClient.invalidateQueries({ queryKey: authKeys.status() });
      void queryClient.invalidateQueries({ queryKey: userKeys.all });
      onSuccess?.();
    },
    onError: () => {
      // Show user-friendly error notification
      addNotification({
        title: 'Login Failed',
        message: 'Please check your credentials and try again',
        type: 'error',
      });
      // Clear auth status on login failure
      queryClient.setQueryData(authKeys.status(), false);
      onError?.();
    },
  });
};

// Register Mutation (only handles authentication)
const useRegister = ({
  onSuccess,
  onError,
}: {
  onSuccess?: () => void;
  onError?: () => void;
} = {}) => {
  const queryClient = useQueryClient();
  const { addNotification } = useNotifications();

  return useMutation({
    mutationFn: (data: RegisterDetails) => register(data),
    mutationKey: authKeys.register(),
    onSuccess: () => {
      // Only invalidate auth status, user data is handled elsewhere
      void queryClient.invalidateQueries({ queryKey: authKeys.status() });
      // Show success notification
      addNotification({
        title: 'Registration Successful',
        message: 'Welcome! Your account has been created successfully',
        type: 'success',
      });
      onSuccess?.();
    },
    onError: () => {
      addNotification({
        title: 'Registration Failed',
        message: 'Please check your details and try again',
        type: 'error',
      });
      onError?.();
    },
  });
};

// Get Registration Details Mutation (only handles authentication)
const useGetRegistrationDetails = ({
  onSuccess,
  onError,
}: {
  onSuccess?: () => void;
  onError?: () => void;
} = {}) => {
  return useMutation({
    mutationFn: (token: string | null) => getRegistrationDetails(token),
    mutationKey: authKeys.registrationDetails(),
    onSuccess: () => {
      onSuccess?.();
    },
    onError: () => {
      onError?.();
    },
  });
};

// Logout Mutation (only handles authentication)
const useLogout = ({
  onSuccess,
  onError,
}: {
  onSuccess?: () => void;
  onError?: () => void;
} = {}) => {
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  const { addNotification } = useNotifications();

  return useMutation({
    mutationFn: () => logout(),
    mutationKey: authKeys.logout(),
    onSuccess: async () => {
      try {
        await navigate(paths.auth.login.getHref());
      } catch {
        noop();
      }
      // Clear user identity from all third-party services (Analytics, Sentry, etc.)
      clearUserIdentity();
      // Clear auth status
      queryClient.setQueryData(authKeys.status(), false);
      // Invalidate all queries
      void queryClient.invalidateQueries();
      // Show success notification
      addNotification({
        title: 'Logged Out',
        message: 'You have been successfully logged out',
        type: 'success',
      });
      onSuccess?.();
    },
    onError: () => {
      addNotification({
        title: 'Logout Failed',
        message: 'Please try again',
        type: 'error',
      });
      onError?.();
    },
  });
};

// Send OTP Mutation
const useSendOTP = ({
  onSuccess,
  onError,
}: {
  onSuccess?: () => void;
  onError?: () => void;
} = {}) => {
  const { addNotification } = useNotifications();

  return useMutation({
    mutationFn: (data: SendOTPData) => sendOTP(data),
    mutationKey: authKeys.sendOtp(),
    onSuccess: () => {
      addNotification({
        title: 'OTP Sent',
        message: 'Please check your phone or email for the verification code',
        type: 'success',
      });
      onSuccess?.();
    },
    onError: () => {
      addNotification({
        title: 'Failed to Send OTP',
        message: 'Please try again',
        type: 'error',
      });
      onError?.();
    },
  });
};

// Resend OTP Mutation
const useResendOTP = ({
  onSuccess,
  onError,
}: {
  onSuccess?: () => void;
  onError?: () => void;
} = {}) => {
  const { addNotification } = useNotifications();

  return useMutation({
    mutationFn: (data: SendOTPData) => resendOTP(data),
    mutationKey: authKeys.resendOtp(),
    onSuccess: () => {
      addNotification({
        title: 'OTP Resent',
        message: 'A new verification code has been sent',
        type: 'success',
      });
      onSuccess?.();
    },
    onError: () => {
      addNotification({
        title: 'Failed to Resend OTP',
        message: 'Please try again',
        type: 'error',
      });
      onError?.();
    },
  });
};

// Submit OTP Mutation (for OTP login)
const useSubmitOTP = ({
  onSuccess,
  onError,
}: {
  onSuccess?: () => void;
  onError?: () => void;
} = {}) => {
  const queryClient = useQueryClient();
  const { addNotification } = useNotifications();

  return useMutation({
    mutationFn: (data: SubmitOTPData | boolean) => submitOTP(data),
    mutationKey: authKeys.submitOtp(),
    onSuccess: () => {
      // Only update auth status, not user data
      void queryClient.invalidateQueries({ queryKey: authKeys.status() });
      addNotification({
        title: 'Login Successful',
        message: 'Welcome back! You have been successfully logged in',
        type: 'success',
      });
      onSuccess?.();
    },
    onError: () => {
      addNotification({
        title: 'Invalid OTP',
        message: 'Please check the code and try again',
        type: 'error',
      });
      onError?.();
    },
  });
};

// Combined OTP Flow Hook (convenience hook for full OTP flow)
const useOTPFlow = () => {
  const sendOTPMutation = useSendOTP();
  const resendOTPMutation = useResendOTP();
  const submitOTPMutation = useSubmitOTP();

  return {
    sendOTP: sendOTPMutation,
    resendOTP: resendOTPMutation,
    submitOTP: submitOTPMutation,
    isLoading:
      sendOTPMutation.isPending ||
      resendOTPMutation.isPending ||
      submitOTPMutation.isPending,
    error:
      sendOTPMutation.error ??
      resendOTPMutation.error ??
      submitOTPMutation.error,
  };
};

const useResetPassword = ({
  onSuccess,
  onError,
}: {
  onSuccess?: () => void;
  onError?: () => void;
} = {}) => {
  const { addNotification } = useNotifications();
  return useMutation({
    mutationFn: (data: ResetPasswordDetails) => resetPassword(data),
    mutationKey: authKeys.resetPassword(),
    onSuccess: () => {
      addNotification({
        title: 'Password Reset Successful',
        message: 'Please login to continue',
        type: 'success',
      });
      onSuccess?.();
    },
    onError: () => {
      addNotification({
        title: 'Password Reset Failed',
        message: 'Please try again',
        type: 'error',
      });
      onError?.();
    },
  });
};

// Auth Hook (only auth concerns - no user data)
export const useAuth = () => {
  const loginMutation = useLogin();
  const registerMutation = useRegister();
  const registrationDetailsMutation = useGetRegistrationDetails();
  const logoutMutation = useLogout();
  const otpFlow = useOTPFlow();
  const resetPasswordMutation = useResetPassword();

  return {
    // Auth operations
    login: loginMutation.mutate,
    register: registerMutation.mutate,
    getRegistrationDetails: registrationDetailsMutation.mutate,
    logout: logoutMutation.mutate,

    // OTP operations
    sendOTP: otpFlow.sendOTP.mutate,
    resendOTP: otpFlow.resendOTP.mutate,
    submitOTP: otpFlow.submitOTP.mutate,
    resetPassword: resetPasswordMutation.mutate,

    // Loading states
    isLoggingIn: loginMutation.isPending,
    isRegistering: registerMutation.isPending,
    isLoggingOut: logoutMutation.isPending,
    isOTPLoading: otpFlow.isLoading,
    isResetPasswordLoading: resetPasswordMutation.isPending,
    isRegistrationDetailsLoading: registrationDetailsMutation.isPending,

    // Error states
    loginError: loginMutation.error,
    registerError: registerMutation.error,
    logoutError: logoutMutation.error,
    otpError: otpFlow.error,
    resetPasswordError: resetPasswordMutation.error,
    registrationDetailsError: registrationDetailsMutation.error,

    // Reset functions
    resetLoginError: loginMutation.reset,
    resetRegisterError: registerMutation.reset,
    resetLogoutError: logoutMutation.reset,
    resetResetPasswordError: resetPasswordMutation.reset,
    resetRegistrationDetailsError: registrationDetailsMutation.reset,
  };
};
